// Port of ios/Sources/ViewModels/TeamMute.swift.
//
// Silencing a WHOLE TEAM (user order 2026-10-05: "an option to mute a team so the notifications
// from that channel don't appear").
//
// Mattermost has no such concept — it mutes a channel (`notify_props.mark_unread`) and a sidebar
// category, and nothing above them. So this is OURS, stored where every other per-reader choice
// on this server lives: a PREFERENCE row (`satori_team_mute` / team id / "true"). That buys three
// things a local flag would not — it follows the account to the reader's other devices, it
// survives a reinstall, and the webapp ignores a category it does not know rather than breaking.
//
// What muting a team does NOT mean: hiding it. The team stays in the strip and its conversations
// stay readable with their unread marks — muting is about what INTERRUPTS, which is the same
// promise a muted channel makes.

/**
 * The preference category. Namespaced, because it is not a Mattermost concept and must never
 * be mistaken for one.
 */
export const preferenceCategory = 'satori_team_mute';

/**
 * Where the local mirror lives, so the UI reads it from SQLite like everything else instead
 * of waiting for the network (docs/PERFORMANCE.md: local-first).
 */
export const syncStateKey = 'muted_teams';

/**
 * Ids from the stored mirror. Tolerates the shapes a stored string actually arrives in —
 * absent, empty, trailing separators, stray whitespace — because a parse failure here would
 * silently un-mute every team at once.
 */
export function decode(raw: string | null | undefined): Set<string> {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== ''),
  );
}

/**
 * SORTED, so the same set always writes the same string: an unstable order would make every
 * save look like a change to anything comparing the stored value.
 */
export function encode(ids: ReadonlySet<string>): string {
  // Plain code-unit order: team ids are ASCII, where it equals Swift's `sorted()`.
  return [...ids].sort().join(',');
}

export function isMuted(teamId: string, muted: ReadonlySet<string>): boolean {
  return teamId !== '' && muted.has(teamId);
}

/**
 * The toggle as a pure transition, so the view model can apply it optimistically and roll it
 * back if the server write fails.
 */
export function toggled(teamId: string, muted: ReadonlySet<string>, newValue: boolean): Set<string> {
  const next = new Set(muted);
  if (teamId === '') return next;
  if (newValue) next.add(teamId);
  else next.delete(teamId);
  return next;
}

/**
 * Whether a notification for a post in `teamId` may be raised at all.
 *
 * A DM or a group message carries NO team (`teamId` is empty on this server, verified in the
 * 1.0.265 work) — and a direct message is exactly what must still get through when somebody
 * silences a team's channels. An empty team id is therefore never muted, which the `isMuted`
 * guard above already encodes; this name is here so the call site reads as the rule it is.
 */
export function allowsNotification(teamId: string, muted: ReadonlySet<string>): boolean {
  return !isMuted(teamId, muted);
}

/** Namespace form, for `TeamMute.decode(...)` call sites. */
export const TeamMute = {
  preferenceCategory,
  syncStateKey,
  decode,
  encode,
  isMuted,
  toggled,
  allowsNotification,
} as const;
